import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { PortalGlobalNotificationClient } from '../../../core/openapi/notification';
import { defer, filter, map, of, shareReplay, tap } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { AuthService } from '../../../core/services/auth/auth.service';
import { UserInformationService } from '../../../core/services/user/user-information.service';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { ResizeService } from '../../services/resize/resize.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LayoutShiftService } from '../../services/layout-shift/layout-shift.service';

const ANIMATION_SPEED_PX_PER_SEC = 50;

@Component({
  selector: 'rrp-system-notification',
  templateUrl: './system-notification.component.html',
  styleUrl: './system-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe],
})
export class SystemNotificationComponent implements AfterViewInit {
  private readonly portalGlobalNotificationClient = inject(
    PortalGlobalNotificationClient,
  );
  private readonly userInformationService = inject(UserInformationService);
  private readonly authService = inject(AuthService);
  private readonly layoutShiftService = inject(LayoutShiftService);
  private readonly resizeService = inject(ResizeService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('container', { static: false })
  private containerRef?: ElementRef<HTMLElement>;
  @ViewChild('content', { static: false })
  private contentRef?: ElementRef<HTMLElement>;

  public message$ = defer(() =>
    this.authService.hasValidAccessToken()
      ? this.userInformationService.getCurrentUser().pipe(
          filter((value) => !!value),
          switchMap(() =>
            this.portalGlobalNotificationClient
              .v1PortalGlobalNotificationGet()
              .pipe(
                catchError(() => of(null)),
                map((response) => response?.description),
                tap((value) =>
                  this.layoutShiftService.containsSystemNotification.set(
                    !!value,
                  ),
                ),
              ),
          ),
        )
      : of(null),
  ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  public readonly isOverflowing: WritableSignal<boolean> = signal(false);
  public readonly animationDuration: WritableSignal<string> = signal('0s');

  public ngAfterViewInit(): void {
    // Subscribe to message changes to update the marquee when the message is loaded
    this.message$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        // Use setTimeout to ensure the view has been rendered after message changes
        setTimeout(() => this.updateMarquee(), 0);
      });

    this.resizeService.innerWidth$
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateMarquee());
  }

  private updateMarquee(): void {
    if (!this.containerRef) {
      console.error(
        'Container reference is undefined - message might be empty or not rendered yet',
      );
      return;
    }

    if (!this.contentRef) {
      console.error(
        'Content reference is undefined - message might be empty or not rendered yet',
      );
      return;
    }

    const containerEl = this.containerRef.nativeElement;
    const contentEl = this.contentRef.nativeElement;
    const overflown = contentEl.scrollWidth > containerEl.clientWidth;

    this.isOverflowing.set(overflown);
    this.animationDuration.set(
      overflown
        ? `${(contentEl.scrollWidth + containerEl.clientWidth) / ANIMATION_SPEED_PX_PER_SEC}s`
        : '0s',
    );
  }
}
